import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Control } from "react-hook-form";
import CustomCheckbox from "../../common/CustomCheckBox";
import CustomRadioButton from "../../common/CustomRadioButton";
import CustomTextField from "../../common/CustomTextField";
import { Box, Grid, Typography, useTheme } from "@mui/material";
import CustomDatePicker from "../../common/CustomDatePicker";
import dayjs from "dayjs";
import { tokens } from "../../../theme";
import { woundNameWithDimensions } from "../../../utils/Utility";

interface QuestionData {
  type: "Multiple Select" | "Single Select" | "Date Input" | "Text";
  questionCode: string;
  prompt: string;
  answers?: {
    answer: AnswerData[];
    optional: boolean;
    multiple_allowed: boolean;
  };
  answer_required: boolean;
}

interface AnswerData {
  key: string;
  value: string;
  question?: QuestionData | QuestionData[];
}

interface Question {
  productQuestionId: number;
  productId: number;
  sequence: number;
  questionCode: string;
  questionInfo: {
    question: QuestionData & {
      questionType: string;
    };
  };
}

interface QuestionComponentProps {
  namePrefix?: string;
  productName: string;
  productQuestions: Question[];
  control: Control<any>;
  getValues: (name: string) => any;
  setValue: (name: any, value: any) => void;
  watch: (name: string) => any;
  isDisabled?: boolean;
  woundData?: any[];
  autoSelectSafeguard?: boolean;
}

const QuestionComponent: React.FC<QuestionComponentProps> = ({
  namePrefix = "",
  productName,
  productQuestions,
  control,
  getValues,
  setValue,
  watch,
  isDisabled = false,
  woundData = [],
  autoSelectSafeguard
}) => {
  const [questionTypes, setQuestionTypes] = useState<string[]>([]);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";
  const accent = "#14b8a6";
  const textPrimary = theme.palette.text.primary;
  const border = theme.palette.divider;
  const radioSx = {
    "&.Mui-checked": { color: `${accent} !important` },
  };

  /* ── Field wrapper sx — built from `colors`/`theme` only (no shared modal-style hook) ── */
  const fieldWrapSx = {
    "& .MuiOutlinedInput-root, & .MuiInputBase-root": {
      backgroundColor: `${isDark ? "rgba(255,255,255,0.04)" : colors.grey[800]} !important`,
      borderRadius: "10px !important",
      fontSize: "0.88rem",
      color: textPrimary,
    },
    "& .MuiOutlinedInput-root fieldset, & .MuiOutlinedInput-notchedOutline": {
      borderColor: `${border} !important`,
      transition: "border-color 0.18s",
    },
    "& .MuiOutlinedInput-root:hover fieldset": {
      borderColor: `${accent} !important`,
    },
    "& .MuiOutlinedInput-root.Mui-focused fieldset": {
      borderColor: `${accent} !important`,
      borderWidth: "1px !important",
    },
    "& .MuiInputLabel-root, & label": { display: "none" },
    "& .MuiInputBase-input": { color: textPrimary, fontSize: "0.88rem", backgroundColor: "transparent !important" },
    "& .MuiSvgIcon-root": { color: theme.palette.text.secondary },
  };
  const datepickerWrapSx = {
    ...fieldWrapSx,
    "& .MuiInputBase-root": { paddingTop: "0 !important" },
  };
  const inputFieldSx = fieldWrapSx;

  const sortedQuestions = useMemo(
    () => [...productQuestions].sort((a, b) => a.sequence - b.sequence),
    [productQuestions]
  );

  useEffect(() => {
    const types: string[] = [];
    productQuestions.forEach((q) => {
      const type = q.questionInfo.question.questionType;
      if (!types.includes(type)) {
        types.push(type);
      }
    });
    setQuestionTypes(types);
  }, [productQuestions]);

  // CS-1643: Removed clearNestedValues - sub-questions should remain independent
  // const clearNestedValues = (fieldName: string) => {
  //   const allValues = getValues(namePrefix);
  //   const rawFieldName = fieldName.split('.')[1];

  //   allValues && Object.keys(allValues).forEach((key) => {
  //     if (key.startsWith(`${rawFieldName}_`) && key !== rawFieldName) {
  //       setValue(`${namePrefix}.${key}`, null);
  //     }
  //   });
  // };

  const handleMultiSelectChange = (
    (fieldName: string, answerValue: string) => {
      const currentValues = getValues(fieldName) || [];
      
      const newValues = currentValues.includes(answerValue)
        ? currentValues.filter((val: any) => val !== answerValue)
        : [...currentValues, answerValue];

      // CS-1643: No longer clearing nested values - questions are independent
      setValue(fieldName, newValues);
    }
  );

  const isChecked = (
    (fieldName: string, answerValue: string) => {
      const selectedValues = getValues(fieldName);
      return Array.isArray(selectedValues) && selectedValues.includes(answerValue);
    }
  );

  const handleDateChange = (
    (date: any, fieldName: string) => {
      if (date) {
        setValue(fieldName, dayjs(date).format("MM/DD/YYYY"));
      }
    }
  );

  const renderNestedQuestions = (
    questions: QuestionData[],
    baseFieldName: string,
    parentAnswerKey: string,
    fullPrefix: string
  ) => {
    return questions.map((nestedQuestion, index) => {
      const fieldName = `${baseFieldName}.${nestedQuestion.questionCode}`;
      return (
        <Grid container key={`${parentAnswerKey}-nested-${index}`} sx={{ mt: 1, ml: 2 }}>
          <Grid item xs={12}>
            <Typography variant="body2" sx={{ mb: 1, color: textPrimary }}>
              {nestedQuestion.prompt}
              {nestedQuestion.answer_required && (
                <Box component="span" sx={{ color: accent, fontSize: "1rem" }}> *</Box>
              )}
            </Typography>
            {renderQuestionField(nestedQuestion, fieldName, true, fullPrefix)}
          </Grid>
        </Grid>
      );
    });
  };

  const renderNestedQuestion = (
    (nestedQuestionData: QuestionData | QuestionData[], baseFieldName: string, parentAnswerKey: string, fullPrefix: string) => {
      if (Array.isArray(nestedQuestionData)) {
        return renderNestedQuestions(nestedQuestionData, baseFieldName, parentAnswerKey, fullPrefix);
      }

      const fieldName = `${baseFieldName}.${nestedQuestionData.questionCode}`;
      return (
        <Grid container key={`${parentAnswerKey}-nested`} sx={{ mt: 1, ml: 2 }}>
          <Grid item xs={12}>
            <Typography variant="body2" sx={{ mb: 1, color: textPrimary }}>
              {nestedQuestionData.prompt}
              {nestedQuestionData.answer_required && (
                <Box component="span" sx={{ color: accent, fontSize: "1rem" }}> *</Box>
              )}
            </Typography>
            {renderQuestionField(nestedQuestionData, fieldName, true, fullPrefix)}
          </Grid>
        </Grid>
      );
    }
  );

  // const posValue = watch("treatmentInformation.placesOfService");

  // useEffect(() => {
  //   const q1 = productQuestions?.find(q => q.questionCode === "Q4236_Q1");
  //   if (autoSelectSafeguard && q1) {
  //     if (posValue === "SNF (31)" || posValue === "NF (32)") {
  //       setValue("safeguardQuestions.Q4236_Q1", "Q4236_Q1_A1");
  //     } else {
  //       clearNestedValues("safeguardQuestions.Q4236_Q1");
  //       setValue("safeguardQuestions.Q4236_Q1", "Q4236_Q1_A2");
  //     }
  //   };
  //   if(!q1){
  //     setValue("safeguardQuestions.Q4236_Q1", null);
  //   };
  // }, [posValue, productQuestions]);

  const renderQuestionField = useMemo(() => {
    return  (
      question: QuestionData,
      fieldName: string,
      isNested: boolean,
      fullPrefix: string
    ) => {
      const { type, answers, answer_required } = question;
      switch (type) {
        case "Multiple Select":
          return (
            <Grid container spacing={2} md={6} pl={2} mt={1}>
              {answers?.answer?.map((answer, i) => (
                <Grid
                  item
                  xs={12}
                  sx={{
                    paddingTop: "0 !important",
                    "& > .MuiGrid-item": {
                      paddingTop: 0
                    }
                  }}
                  md={6}
                  key={i}
                >
                  <Grid container alignItems="center" spacing={1}>
                    <Grid item>
                      <CustomCheckbox
                        control={control}
                        name={fieldName}
                        onChange={() => handleMultiSelectChange(fieldName, answer.key)}
                        id={`${fieldName}_${answer.value}`}
                        checked={isChecked(fieldName, answer.key)}
                        label={answer.value}
                        rules={answer_required ? { required: "This field is required" } : {}}
                        isDisabled={isDisabled}
                      />
                    </Grid>
                  </Grid>
                  
                  {answer.question && isChecked(fieldName, answer.key) && (
                    renderNestedQuestion(
                      answer.question,
                      fullPrefix,
                      answer.key,
                      fullPrefix
                    )
                  )}
                </Grid>
              ))}
            </Grid>
          );

        case "Single Select":
          return (
            <div style={{ paddingLeft: "20px" }}>
              <Grid container>
                <Grid item>
                  <CustomRadioButton
                    control={control}
                    controllerName={fieldName}
                    options={answers?.answer?.map((ans) => ({
                      value: ans.key,
                      label: ans.value
                    }))}
                    // CS-1643: No longer clearing nested values - questions are independent
                    rules={answer_required ? { required: "This field is required" } : {}}
                    isDisabled={isDisabled}
                    radioSx={radioSx}
                  />
                </Grid>
                <Grid item md={12}></Grid>

                {answers?.answer?.map((ans) => {
                  if (!ans.question) return null;

                  const selectedValue = watch(fieldName);
                  if (selectedValue !== ans.key) return null;

                  return renderNestedQuestion(
                    ans.question,
                    fullPrefix,
                    ans.key,
                    fullPrefix
                  );
                })}
              </Grid>
            </div>
          );

        case "Date Input":
          return (
            <Grid container>
              <Grid item xs={12} sm={5} md={2} pt={1} pl={2}>
                <Box sx={datepickerWrapSx}>
                  <CustomDatePicker
                    controllerName={fieldName}
                    control={control}
                    label=""
                    size="small"
                    rules={answer_required ? { required: "This field is required" } : {}}
                    onChange={(e) => handleDateChange(e, fieldName)}
                    disabled={isDisabled}

                  />
                </Box>
              </Grid>
            </Grid>
          );

        case "Text":
          return (
            <Grid container>
              <Grid item xs={12} sm={5} md={2} pt={1} pl={2}>
                <Box sx={inputFieldSx}>
                  <CustomTextField
                    control={control}
                    controllerName={fieldName}
                    label=""
                    size="small"
                    rules={answer_required ? { required: "This field is required" } : {}}
                    isDisabled={isDisabled}
                    fullWidth
                  />
                </Box>
              </Grid>
            </Grid>
          );

        default:
          return <Typography>Unsupported question type: {type}</Typography>;
      }
    }
  }
   ,
    [namePrefix, control, isChecked, isDisabled, renderNestedQuestion, radioSx, datepickerWrapSx, inputFieldSx, handleMultiSelectChange, handleDateChange, watch]
  );

  const getNestedFieldSuffix = ((type: string) => {
    switch (type) {
      case "Text":
        return "OTHER";
      case "Date Input":
        return "DATE";
      default:
        return "";
    }
  });

  const QuestionRenderer = useMemo(() => {
    return (
      ({
        question,
        contextPrefix = "",
        contextTitle = "",
        contextDisplay = null
      }: {
        question: Question;
        contextPrefix?: string;
        contextTitle?: string;
        contextDisplay?: React.ReactNode | null;
      }) => {
        const { prompt, answer_required } = question.questionInfo.question;
        const fullPrefix = contextPrefix ? `${namePrefix}.${contextPrefix}` : namePrefix;
        const fieldName = `${fullPrefix}.${question.questionCode}`;

        return (
          <Box sx={{ mb: 2 }}>
            {contextTitle && <Typography sx={{ fontSize: "0.95rem", mb: 1, color: textPrimary }}>{contextTitle}</Typography>}
            {contextDisplay}
            <Typography sx={{ fontSize: "0.88rem", color: textPrimary, display: "flex", alignItems: "center", gap: 0.4 }}>
              {prompt}
              {answer_required && (
                <Box component="span" sx={{ color: accent }}>*</Box>
              )}
            </Typography>

            {renderQuestionField(question.questionInfo.question, fieldName, false, fullPrefix)}
          </Box>
        );
      }
    )
  }, [namePrefix, textPrimary, accent, renderQuestionField]);

  const questionsByType = useMemo(() => {
    const grouped: Record<string, Question[]> = {};
    sortedQuestions.forEach(question => {
      const type = question.questionInfo.question.questionType;
      if (!grouped[type]) {
        grouped[type] = [];
      }
      grouped[type].push(question);
    });
    return grouped;
  }, [sortedQuestions]);

  /* CS-1426: The section title/card is now owned by the caller (IVRForm renders
     this inside its own cardSx + SectionHeader) — this component only renders
     the question list content itself. */
  return (
    <Box>
      {sortedQuestions
        .filter(question => question.questionInfo.question.questionType !== "WOUND_INFO")
        .map((question, index) => (
          <QuestionRenderer key={`${index}-${question.questionCode}`} question={question} />
        ))}

      {questionsByType["WOUND_INFO"] && woundData?.map((wound, woundIndex) => (
        <Box
          key={woundIndex}
          sx={{
            mt: woundIndex === 0 ? 0 : 2.5,
            mb: 1,
            borderRadius: "12px",
            border: `1px solid ${border}`,
            backgroundColor: isDark ? "rgba(255,255,255,0.02)" : colors.grey[800],
            overflow: "hidden",
          }}
        >
          <Box sx={{ px: 2, py: 1.2, borderBottom: `1px solid ${border}` }}>
            <Typography sx={{ fontSize: "0.86rem", color: textPrimary }}>
              {woundNameWithDimensions(
                wound.woundType,
                wound.woundInfoKey,
                wound.length,
                wound.width,
                wound.depth
              )}
            </Typography>
          </Box>
          <Box sx={{ px: 2, py: 1.8 }}>
            {questionsByType["WOUND_INFO"].map((question, index) => (
              <QuestionRenderer
                key={index}
                question={question}
                contextPrefix={`WOUNDS[${wound.woundInfoKey}]`}
              />
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default QuestionComponent;